// Empty-state copy catalog (Story 2.12).
//
// One export por surface. Voice contract (UX-DR42):
//   - No emojis.
//   - No "!".
//   - No celebratory / AI-suggestion / apologetic tone.
//   - Direct, unfussy, professional (Plausible-esque).
//
// La consumibilidad de este catálogo se valida en
// `test/unit/utils/emptyStateCopy.spec.js`: scan automático rechaza
// cualquier emoji o exclamation mark.

// Hero panels (Epic 2) -------------------------------------------------

export const RAGE_CLICK_EMPTY = Object.freeze({
  headline: 'No rage clicks recorded in this window.',
  body: 'This only means none were observed in the selected period.',
  action: 'larger-window',
});

export const DEAD_CLICK_EMPTY = Object.freeze({
  headline: 'No dead clicks recorded in this window.',
  body: 'This only means none were observed in the selected period.',
  action: 'larger-window',
});

export const PAGEVIEWS_EMPTY = Object.freeze({
  headline: 'No pageviews recorded in this window.',
  body: 'Check a larger period or review the integration guide if this is unexpected.',
  action: 'larger-window',
});

export const PULSE_EMPTY = Object.freeze({
  headline: 'No live sessions right now.',
  body: 'No sessions were observed in the last 5 minutes.',
});

// Partial-period footnote (FR18) — function porque interpola la
// duración relativa.
export function partialPeriodFootnote(relative) {
  return `Showing data since first event, ${relative}.`;
}

// Insight panels (KPI strip + rankings) ---------------------------------

export const SUMMARY_EMPTY = Object.freeze({
  headline: 'No sessions recorded in this window.',
  body: 'No session activity was observed in the selected period.',
  action: 'larger-window',
});

export const TOP_PAGES_EMPTY = Object.freeze({
  headline: 'No pageviews in this window.',
  body: 'Top pages are ranked from pageview events.',
});

export const REFERRERS_EMPTY = Object.freeze({
  headline: 'No traffic sources in this window.',
  body: 'Referrers are derived from pageview events.',
});

export const TECH_EMPTY = Object.freeze({
  headline: 'No sessions to profile in this window.',
  body: 'Browser, OS and device splits appear with traffic.',
});

export const AUDIENCE_EMPTY = Object.freeze({
  headline: 'No audience data in this window.',
  body: 'Languages and timezones are aggregated from sessions.',
});

export const CUSTOM_EVENTS_EMPTY = Object.freeze({
  headline: 'No custom events in this window.',
  body: 'Instrument events with Tracker.track(name) to see them here.',
});

export const MODULE_USAGE_EMPTY = Object.freeze({
  headline: 'No active modules in the catalog.',
  body: 'Seed or configure the module catalog to measure usage.',
});

export const ANOMALIES_EMPTY = Object.freeze({
  headline: 'No anomalies detected in this window.',
  body: 'Spikes in errors, rage or dead clicks surface here as they emerge.',
});

// Single-source map para iterar en tests + auditorías.
export const HERO_PANEL_COPY = Object.freeze({
  rageClick: RAGE_CLICK_EMPTY,
  deadClick: DEAD_CLICK_EMPTY,
  pageviews: PAGEVIEWS_EMPTY,
  pulse: PULSE_EMPTY,
});

export const INSIGHT_PANEL_COPY = Object.freeze({
  summary: SUMMARY_EMPTY,
  topPages: TOP_PAGES_EMPTY,
  referrers: REFERRERS_EMPTY,
  tech: TECH_EMPTY,
  audience: AUDIENCE_EMPTY,
  customEvents: CUSTOM_EVENTS_EMPTY,
  moduleUsage: MODULE_USAGE_EMPTY,
  anomalies: ANOMALIES_EMPTY,
});

// Admin surfaces (Epic 4) ----------------------------------------------

// Story 4.3 / FR48a / UX-DR31 — apps list empty.
export const APP_LIST_EMPTY = Object.freeze({
  headline: 'No apps provisioned yet.',
  body: 'Create the first app to begin ingesting events.',
});

// T001-4 — clients (`tenants`) list empty.
export const CLIENT_LIST_EMPTY = Object.freeze({
  headline: 'No clients yet.',
  body: 'Create a client to group its apps and give its people access.',
});

// Story 4.14 / UX-DR31 — audit log no actions surface. Body omitted per
// spec (filtered runs land here too — no need to suggest "create one").
export const AUDIT_LOG_EMPTY = Object.freeze({
  headline: 'No admin actions recorded.',
  body: null,
});

// Story 6.15 / FR68f / UX-DR42 — heatmap surface cuando el tenant
// NO tiene screenshot data. Voice contract: no emojis, no "!",
// ni tono apologético.
export const HEATMAP_NO_SCREENSHOT_EMPTY = Object.freeze({
  headline: 'No screenshot available.',
  body: 'Opt-in capture is configured per tenant. See the integration guide for setup.',
  primaryCta: { label: 'Integration guide', href: '/guide/integration' },
  secondaryCta: { label: 'Back to dashboard', href: '/' },
});

// T001-2 — panel de semáforo cuando la app no tiene módulos activos en
// el catálogo. Sin módulos no hay veredicto posible; la acción está en
// el catálogo, no en el panel.
export const SERVICE_HEALTH_EMPTY = Object.freeze({
  headline: 'No modules in the catalog yet.',
  body: 'Add modules and path rules for this app to get a per-module verdict.',
});

// F2 / U2.10 — panel interno de estabilidad de los cambios: sin
// code-project configurado (o sin ningún pipeline/despliegue/commit
// todavía en la ventana) no hay nada que desglosar.
export const DELIVERY_EMPTY = Object.freeze({
  headline: 'No delivery activity in this window yet.',
  body: 'This fills in once the code project is configured and pipelines/deployments start flowing in.',
});
